import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { AuthPage } from "../components/AuthPage";
import { Banner } from "../components/ui/Banner";
import { Button } from "../components/ui/Button";
import { useSuccessToast } from "../components/ui/Toast";
import {
  choosePassword,
  credentialFailure,
  readPasswordLink,
} from "../lib/credentials";

import styles from "./reset-password.module.css";

type CodeState =
  | { status: "checking" }
  | { status: "dead" }
  | { status: "ready"; email: string };

/**
 * Where a reset link lands, and an invite link too: both are one-time
 * codes for choosing a password, minted by the API and addressed here
 * rather than to Firebase's own page.
 */
export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const oobCode = params.get("oobCode") ?? "";
  const navigate = useNavigate();
  const toast = useSuccessToast();
  const [codeState, setCodeState] = useState<CodeState>({ status: "checking" });
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!oobCode) {
      setCodeState({ status: "dead" });
      return;
    }
    let live = true;
    // An unconfigured build throws here as well, and reads as a dead link.
    Promise.resolve()
      .then(() => readPasswordLink(oobCode))
      .then((email) => live && setCodeState({ status: "ready", email }))
      .catch(() => live && setCodeState({ status: "dead" }));
    return () => {
      live = false;
    };
  }, [oobCode]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (codeState.status !== "ready") return;
    setError(null);
    setSubmitting(true);
    try {
      await choosePassword(oobCode, password);
      toast("Password saved. Log in with your new password.");
      navigate("/login", {
        replace: true,
        state: { email: codeState.email },
      });
    } catch (err) {
      const failure = credentialFailure(err);
      if (failure === "weak_password") {
        setError("Use at least 6 characters.");
      } else if (failure === "dead_link") {
        setCodeState({ status: "dead" });
      } else {
        setError("Could not save that password. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (codeState.status === "checking") {
    return (
      <AuthPage title="Choose a password">
        <p className={styles.loading}>Loading</p>
      </AuthPage>
    );
  }

  if (codeState.status === "dead") {
    return (
      <AuthPage title="Choose a password">
        <div className={styles.stack}>
          <Banner variant="error">
            This link has expired or has already been used.
          </Banner>
          <p className={styles.footNote}>
            <Link className={styles.footLink} to="/forgot-password">
              Get a new link
            </Link>
          </p>
        </div>
      </AuthPage>
    );
  }

  return (
    <AuthPage
      title="Choose a password"
      description={`For ${codeState.email}`}
    >
      <form onSubmit={onSubmit} className={styles.stack}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>New password</span>
          <input
            autoComplete="new-password"
            autoFocus
            className={styles.input}
            disabled={submitting}
            minLength={6}
            onChange={(event) => setPassword(event.target.value)}
            required
            type="password"
            value={password}
          />
        </label>

        {error ? <Banner variant="error">{error}</Banner> : null}

        <div className={styles.actions}>
          <Button
            className={styles.submit}
            disabled={submitting}
            loading={submitting}
            type="submit"
          >
            Save password
          </Button>
        </div>
      </form>
    </AuthPage>
  );
}
